import { useAuth } from '@agentnet/auth';
import { getFincenReport } from 'api/fincen-report-api';
import {
  CertificationFormData,
  FincenResponse,
  PaymentInformation,
  PropertyData,
  ReportingPartyFormData,
  Transferee,
  TransfereeParty,
  Transferor,
} from 'features/files/fincen-reporting/types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { certificationFormSchema } from 'utilities/validation/schemas/fincen-certifier-schema';
import { paymentSchema } from 'utilities/validation/schemas/fincen-payment-schema';
import propertyAddressSchema from 'utilities/validation/schemas/fincen-property-address-schema';
import reportingPartySchema from 'utilities/validation/schemas/fincen-reporting-party-schema';
import transfereeSchema from 'utilities/validation/schemas/fincen-transferee-schema';
import { transferorSchema } from 'utilities/validation/schemas/fincen-transferor-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import * as yup from 'yup';
import useAsync, { NoSessionError } from './useAsync';

export interface FincenReportContextInterface {
  fincenReport: FincenResponse | null;
  fincenReportStatus: 'idle' | 'pending' | 'success' | 'error';
  currentFileId: string | null;
  hasValidationResults: boolean;
  loadFincenReport: (fileId: string) => Promise<void>;
  clearFincenReport: () => void;
  reloadFincenReport: (fileId: string) => Promise<void>;
  updateReportingParty: (reportingPartyData: ReportingPartyFormData) => void;
  updateTransferee: (transfereeData: Transferee[]) => void;
  updateTransferor: (transferorData: Transferor[]) => void;
  updatePaymentInformation: (paymentData: PaymentInformation) => void;
  updatePropertyInformation: (propertyData: PropertyData[]) => void;
  updateCertifier: (certifierData: CertificationFormData) => void;
  reportingPartyValidationErrors: FieldValidationError[];
  setReportingPartyValidationErrors: (errors: FieldValidationError[]) => void;
  clearReportingPartyValidationErrors: () => void;
  propertyAddressValidationErrors: FieldValidationError[];
  setPropertyAddressValidationErrors: (errors: FieldValidationError[]) => void;
  clearPropertyAddressValidationErrors: () => void;
  transferorValidationErrors: FieldValidationError[];
  setTransferorValidationErrors: (errors: FieldValidationError[]) => void;
  clearTransferorValidationErrors: () => void;
  transfereeValidationErrors: FieldValidationError[];
  setTransfereeValidationErrors: (errors: FieldValidationError[]) => void;
  clearTransfereeValidationErrors: () => void;
  paymentValidationErrors: FieldValidationError[];
  setPaymentValidationErrors: (errors: FieldValidationError[]) => void;
  clearPaymentValidationErrors: () => void;
  certifierValidationErrors: FieldValidationError[];
  setCertifierValidationErrors: (errors: FieldValidationError[]) => void;
  clearCertifierValidationErrors: () => void;
  generateButtonClicked: boolean;
  setGenerateButtonClicked: (clicked: boolean) => void;
  autoValidationTriggered: boolean;
  setAutoValidationTriggered: (triggered: boolean) => void;
  validateAllSections: () => Promise<void>;
  showSummary: boolean;
  setShowSummary: (show: boolean) => void;
  useShouldShowSummary: () => boolean;
  registerSaveHandler: (section: string, handler: () => Promise<void>) => void;
  unregisterSaveHandler: (section: string) => void;
  pendingNavigation: string | null;
  setPendingNavigation: (path: string | null) => void;
}

interface FincenReportProviderProps {
  children: React.ReactNode;
}

const FincenReportContext = createContext<FincenReportContextInterface | null>(null);

/**
 * Transform party data from API format to UI format
 * Flatten 'individual' object to root level for UI consumption
 */
const transformPartyFromAPI = (party: TransfereeParty): TransfereeParty => {
  if (party.individual) {
    const { individual, ...rest } = party;
    return {
      ...rest,
      firstName: individual.firstName,
      middleName: individual.middleName,
      lastName: individual.lastName,
      suffix: individual.suffix,
    };
  }
  return party;
};

/**
 * Transform transferee data from API format to UI format
 */
const transformTransfereesFromAPI = (transferees?: Transferee[]): Transferee[] | undefined => {
  if (!transferees) return transferees;
  return transferees.map((transferee) => ({
    ...transferee,
    parties: transferee.parties?.map((party) => transformPartyFromAPI(party)),
  }));
};

const FincenReportProvider = ({ children }: FincenReportProviderProps): JSX.Element => {
  const { getAccessToken, signOut } = useAuth();
  const [currentFileId, setCurrentFileId] = useState<string | null>(null);

  const getFincenReportLsKey = (fileId: string) => `agentnet:fincenReport:${fileId}`;

  const [fincenReport, setFincenReport] = useState<FincenResponse | null>(null);

  const [reportingPartyValidationErrors, setReportingPartyValidationErrors] = useState<FieldValidationError[]>([]);
  const [propertyAddressValidationErrors, setPropertyAddressValidationErrors] = useState<FieldValidationError[]>([]);
  const [transfereeValidationErrors, setTransfereeValidationErrors] = useState<FieldValidationError[]>([]);
  const [transferorValidationErrors, setTransferorValidationErrors] = useState<FieldValidationError[]>([]);
  const [paymentValidationErrors, setPaymentValidationErrors] = useState<FieldValidationError[]>([]);
  const [certifierValidationErrors, setCertifierValidationErrors] = useState<FieldValidationError[]>([]);
  const [generateButtonClicked, setGenerateButtonClicked] = useState<boolean>(false);
  const [autoValidationTriggered, setAutoValidationTriggered] = useState<boolean>(false);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [saveHandlers, setSaveHandlers] = useState<Map<string, () => Promise<void>>>(new Map());
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [hasValidationResults, setHasValidationResults] = useState<boolean>(false);

  const getFincenReportData = useCallback(
    async (fileId: string): Promise<FincenResponse | null> => {
      const token = await getAccessToken();
      const response = await getFincenReport(fileId, token);

      // Transform the response to flatten individual objects
      if (response && response.transferee) {
        response.transferee = transformTransfereesFromAPI(response.transferee) as Transferee[];
      }

      return response ?? null;
    },
    [getAccessToken],
  );

  const {
    execute: fetchFincenReport,
    status: fincenReportStatus,
    value: fetchedReport,
    errors: fincenReportErrors = [],
  } = useAsync<FincenResponse | null>(getFincenReportData, false);

  const handleFetchReport = useCallback(
    async (fileId: string, shouldThrow = false): Promise<void> => {
      try {
        if (currentFileId && currentFileId !== fileId) {
          setFincenReport(null);
          setReportingPartyValidationErrors([]);
          setPropertyAddressValidationErrors([]);
          setTransfereeValidationErrors([]);
          setTransferorValidationErrors([]);
          setPaymentValidationErrors([]);
          setCertifierValidationErrors([]);
          setGenerateButtonClicked(false);
          setAutoValidationTriggered(false);
          setHasValidationResults(false);
        }

        setCurrentFileId(fileId);
        const stored = sessionStorage.getItem(getFincenReportLsKey(fileId));
        if (stored) {
          const parsedReport = JSON.parse(stored);

          if (parsedReport && parsedReport.transferee) {
            parsedReport.transferee = transformTransfereesFromAPI(parsedReport.transferee) as Transferee[];
          }
          setFincenReport(parsedReport);
        }
        await fetchFincenReport(fileId);
      } catch (err: unknown) {
        if (err instanceof NoSessionError) {
          await signOut().catch(() => void 0);
        }
        if (shouldThrow) throw err;
      }
    },
    [fetchFincenReport, signOut, currentFileId],
  );

  const loadFincenReport = useCallback(
    (fileId: string): Promise<void> => handleFetchReport(fileId, true),
    [handleFetchReport],
  );
  const reloadFincenReport = useCallback(
    (fileId: string): Promise<void> => handleFetchReport(fileId),
    [handleFetchReport],
  );

  useEffect(() => {
    if (fetchedReport !== null && currentFileId) {
      setFincenReport(fetchedReport);
      sessionStorage.setItem(getFincenReportLsKey(currentFileId), JSON.stringify(fetchedReport));

      // Trigger auto-validation for reports with status 9301 , 9302- Submitted. Generated Report
      if (
        (fetchedReport.reportStatusCdId === 9301 || fetchedReport.reportStatusCdId === 9302) &&
        !autoValidationTriggered
      ) {
        setAutoValidationTriggered(true);
      }
      // Clear validation states and errors when status is 9300 (Open state) - happens when "Edit Report" is clicked
      else if (fetchedReport.reportStatusCdId === 9300) {
        setAutoValidationTriggered(false);
        setGenerateButtonClicked(false);
        setReportingPartyValidationErrors([]);
        setPropertyAddressValidationErrors([]);
        setTransfereeValidationErrors([]);
        setTransferorValidationErrors([]);
        setPaymentValidationErrors([]);
        setCertifierValidationErrors([]);
      }
    }
  }, [fetchedReport, currentFileId, autoValidationTriggered]);

  // Define validation function before using it
  const validateAllSections = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;

    const transfereeArraySchema = yup.array().of(transfereeSchema);
    const transferorArraySchema = yup.array().of(transferorSchema);
    const propertyAddressArraySchema = yup.array().of(propertyAddressSchema);

    const getValidatableData = (data: any, isArray: boolean) => {
      if (isArray) {
        return data && data.length > 0 ? data : [{}];
      }
      return data ?? {};
    };

    // Validate all sections
    const [reportingPartyErrors, propertyErrors, transfereeErrors, transferorErrors, paymentErrors, certifierErrors] =
      await Promise.all([
        doValidate(fincenReport.reportingParty, reportingPartySchema),
        doValidate(getValidatableData(fincenReport.propertyAddresses, true), propertyAddressArraySchema),
        doValidate(getValidatableData(fincenReport.transferee, true), transfereeArraySchema),
        doValidate(getValidatableData(fincenReport.transferor, true), transferorArraySchema),
        doValidate(getValidatableData(fincenReport.paymentDetails, false), paymentSchema),
        doValidate(fincenReport.reportCertifier, certificationFormSchema),
      ]);

    // Set validation errors in context
    setReportingPartyValidationErrors(reportingPartyErrors || []);
    setPropertyAddressValidationErrors(propertyErrors || []);
    setTransfereeValidationErrors(transfereeErrors || []);
    setTransferorValidationErrors(transferorErrors || []);
    setPaymentValidationErrors(paymentErrors || []);
    setCertifierValidationErrors(certifierErrors || []);
    setHasValidationResults(true);
  }, [
    fincenReport,
    setReportingPartyValidationErrors,
    setPropertyAddressValidationErrors,
    setTransfereeValidationErrors,
    setTransferorValidationErrors,
    setPaymentValidationErrors,
    setCertifierValidationErrors,
  ]);

  // Auto-validation effect - runs validation whenever fincenReport data changes
  // This ensures badges always reflect current field completion status
  // Individual form components also handle their own validation on user input
  useEffect(() => {
    if (fincenReport) {
      validateAllSections();
    }
  }, [fincenReport, validateAllSections]);

  const clearFincenReport = useCallback((): void => {
    setFincenReport(null);
    if (currentFileId) {
      sessionStorage.removeItem(getFincenReportLsKey(currentFileId));
    }
    setCurrentFileId(null);
    setReportingPartyValidationErrors([]);
    setTransfereeValidationErrors([]);
    setPropertyAddressValidationErrors([]);
    setTransferorValidationErrors([]);
    setPaymentValidationErrors([]);
    setCertifierValidationErrors([]);
    setGenerateButtonClicked(false);
    setAutoValidationTriggered(false);
    setHasValidationResults(false);
  }, [currentFileId]);

  const clearReportingPartyValidationErrors = useCallback((): void => {
    setReportingPartyValidationErrors([]);
  }, []);

  const clearPropertyAddressValidationErrors = useCallback((): void => {
    setPropertyAddressValidationErrors([]);
  }, []);

  const clearTransferorValidationErrors = useCallback((): void => {
    setTransferorValidationErrors([]);
  }, []);

  const clearTransfereeValidationErrors = useCallback((): void => {
    setTransfereeValidationErrors([]);
  }, []);

  const clearPaymentValidationErrors = useCallback((): void => {
    setPaymentValidationErrors([]);
  }, []);

  const clearCertifierValidationErrors = useCallback((): void => {
    setCertifierValidationErrors([]);
  }, []);

  const updateFincenReportData = useCallback(
    (updateFn: (prevReport: FincenResponse) => Partial<FincenResponse>): void => {
      setFincenReport((prevReport) => {
        if (!prevReport) return prevReport;
        const updatedReport = {
          ...prevReport,
          ...updateFn(prevReport),
        } as FincenResponse;
        if (currentFileId) {
          sessionStorage.setItem(getFincenReportLsKey(currentFileId), JSON.stringify(updatedReport));
        }
        return updatedReport;
      });
    },
    [currentFileId],
  );

  const updateReportingParty = useCallback(
    (reportingPartyData: ReportingPartyFormData): void => {
      updateFincenReportData(() => ({ reportingParty: reportingPartyData }));
    },
    [updateFincenReportData],
  );

  const updateTransferee = useCallback(
    (transfereeData: Transferee[]): void => {
      updateFincenReportData(() => ({ transferee: transfereeData }));
    },
    [updateFincenReportData],
  );

  const updateTransferor = useCallback(
    (transferorData: Transferor[]): void => {
      updateFincenReportData(() => ({ transferor: transferorData }));
    },
    [updateFincenReportData],
  );

  const updatePaymentInformation = useCallback(
    (paymentData: PaymentInformation): void => {
      updateFincenReportData(() => ({ paymentDetails: paymentData }));
    },
    [updateFincenReportData],
  );

  const updatePropertyInformation = useCallback(
    (propertyData: PropertyData[]): void => {
      updateFincenReportData(() => ({ propertyAddresses: propertyData.map((prop) => prop.address) }));
    },
    [updateFincenReportData],
  );

  const updateCertifier = useCallback(
    (certifierData: CertificationFormData): void => {
      updateFincenReportData(() => ({ reportCertifier: certifierData }));
    },
    [updateFincenReportData],
  );

  const useShouldShowSummary = useCallback((): boolean => {
    return fincenReport?.reportStatusCdId === 9302;
  }, [fincenReport?.reportStatusCdId]);

  const conditionalSetShowSummary = useCallback(
    (show: boolean) => {
      if (show && fincenReport?.reportStatusCdId === 9302) {
        setShowSummary(true);
      } else if (!show) {
        setShowSummary(false);
      }
    },
    [fincenReport?.reportStatusCdId],
  );

  const registerSaveHandler = useCallback((section: string, handler: () => Promise<void>) => {
    setSaveHandlers((prev) => {
      const newHandlers = new Map(prev);
      newHandlers.set(section, handler);
      return newHandlers;
    });
  }, []);

  const unregisterSaveHandler = useCallback((section: string) => {
    setSaveHandlers((prev) => {
      const newHandlers = new Map(prev);
      newHandlers.delete(section);
      return newHandlers;
    });
  }, []);

  return (
    <FincenReportContext.Provider
      value={useMemo(
        () => ({
          fincenReport,
          fincenReportStatus,
          currentFileId,
          loadFincenReport,
          clearFincenReport,
          reloadFincenReport,
          hasValidationResults,
          updateReportingParty,
          updateTransferee,
          updateTransferor,
          updatePaymentInformation,
          updatePropertyInformation,
          updateCertifier,
          reportingPartyValidationErrors,
          setReportingPartyValidationErrors,
          clearReportingPartyValidationErrors,
          propertyAddressValidationErrors,
          setPropertyAddressValidationErrors,
          clearPropertyAddressValidationErrors,
          transferorValidationErrors,
          setTransferorValidationErrors,
          clearTransferorValidationErrors,
          transfereeValidationErrors,
          setTransfereeValidationErrors,
          clearTransfereeValidationErrors,
          paymentValidationErrors,
          setPaymentValidationErrors,
          clearPaymentValidationErrors,
          certifierValidationErrors,
          setCertifierValidationErrors,
          clearCertifierValidationErrors,
          generateButtonClicked,
          setGenerateButtonClicked,
          autoValidationTriggered,
          setAutoValidationTriggered,
          validateAllSections,
          showSummary,
          setShowSummary: conditionalSetShowSummary,
          useShouldShowSummary,
          registerSaveHandler,
          unregisterSaveHandler,
          pendingNavigation,
          setPendingNavigation,
        }),
        [
          fincenReport,
          fincenReportStatus,
          currentFileId,
          loadFincenReport,
          clearFincenReport,
          reloadFincenReport,
          hasValidationResults,
          updateReportingParty,
          updateTransferee,
          updateTransferor,
          updatePaymentInformation,
          updatePropertyInformation,
          updateCertifier,
          reportingPartyValidationErrors,
          setReportingPartyValidationErrors,
          clearReportingPartyValidationErrors,
          propertyAddressValidationErrors,
          setPropertyAddressValidationErrors,
          clearPropertyAddressValidationErrors,
          transferorValidationErrors,
          setTransferorValidationErrors,
          clearTransferorValidationErrors,
          transfereeValidationErrors,
          setTransfereeValidationErrors,
          clearTransfereeValidationErrors,
          paymentValidationErrors,
          setPaymentValidationErrors,
          clearPaymentValidationErrors,
          certifierValidationErrors,
          setCertifierValidationErrors,
          clearCertifierValidationErrors,
          generateButtonClicked,
          setGenerateButtonClicked,
          autoValidationTriggered,
          setAutoValidationTriggered,
          validateAllSections,
          showSummary,
          conditionalSetShowSummary,
          useShouldShowSummary,
          registerSaveHandler,
          unregisterSaveHandler,
          pendingNavigation,
        ],
      )}
    >
      {children}
    </FincenReportContext.Provider>
  );
};

export const useFincenReport = (): FincenReportContextInterface => {
  const ctx = useContext(FincenReportContext);
  if (!ctx) throw new Error('useFincenReport must be used within FincenReportProvider');
  return ctx;
};

export { FincenReportContext, FincenReportProvider };
